import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar } from "lucide-react";
import { publicApi, formatDate } from "../../lib/api";
import { SectionHeading, StoryCard, CtaBand } from "../../components/marketing/Blocks";
import { Button } from "../../components/ui/button";

export function StoriesList() {
  const [stories, setStories] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    publicApi.stories().then(setStories).catch(() => setError("Could not load stories right now."));
  }, []);
  return (
    <section className="kt-container py-16 lg:py-24">
      <SectionHeading eyebrow="Community stories" title="Voices from our community." lead="Drivers, restaurant owners and customers on what KohimaTaxi means to them." />
      {error && <p className="mt-8 text-sm text-destructive" data-testid="stories-error">{error}</p>}
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="stories-grid">
        {stories === null && [0, 1, 2].map((i) => <div key={i} className="kt-card h-80 animate-pulse bg-surface-200" />)}
        {stories?.map((s, i) => <StoryCard key={s.story_id} story={s} featured={i === 0} />)}
        {stories?.length === 0 && <p className="text-ink-soft">No stories published yet.</p>}
      </div>
    </section>
  );
}

export default function StoryDetail() {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [others, setOthers] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setStory(null);
    setError("");
    publicApi.story(slug).then(setStory).catch(() => setError("Story not found."));
    publicApi.stories().then((all) => setOthers(all.filter((s) => s.slug !== slug).slice(0, 2))).catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <section className="kt-container py-24 text-center">
        <h1 className="kt-h2">Story not found</h1>
        <Button asChild variant="outline" className="mt-6 rounded-full"><Link to="/stories"><ArrowLeft className="mr-2 h-4 w-4" /> Back to stories</Link></Button>
      </section>
    );
  }
  if (!story) return <div className="kt-container py-24"><div className="mx-auto h-96 max-w-3xl animate-pulse rounded-3xl bg-surface-200" /></div>;

  const paragraphs = String(story.body || "").split(/\n{2,}|\n/).filter(Boolean);

  return (
    <article data-testid="story-detail">
      <div className="kt-container pt-10">
        <Link to="/stories" className="inline-flex items-center text-sm font-medium text-ink-soft hover:text-brand"><ArrowLeft className="mr-1.5 h-4 w-4" /> All stories</Link>
      </div>
      <header className="kt-container mx-auto max-w-3xl pt-8 text-center">
        <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-ink-muted">
          {story.author_role && <span className="rounded-full bg-graphite-50 px-2.5 py-1 font-medium text-graphite">{story.author_role}</span>}
          {story.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{story.location}</span>}
          {story.published_at && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(story.published_at, { timeStyle: undefined })}</span>}
        </div>
        <h1 className="kt-h1 mt-5 !text-4xl sm:!text-5xl">{story.title}</h1>
        {story.subtitle && <p className="kt-lead mt-5">{story.subtitle}</p>}
        <p className="mt-6 text-sm font-semibold text-ink">By {story.author_name}</p>
      </header>
      {story.hero_image_url && (
        <div className="kt-container mt-10">
          <img src={story.hero_image_url} alt={story.title} className="mx-auto aspect-[21/9] w-full max-w-5xl rounded-[1.75rem] object-cover shadow-xl shadow-ink/10" />
        </div>
      )}
      <div className="kt-container mx-auto max-w-3xl py-14">
        <div className="space-y-6 text-lg leading-relaxed text-ink-soft">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
        {story.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {story.tags.map((t) => <span key={t} className="rounded-full border border-surface-300 px-3 py-1 text-xs font-medium text-ink-soft">#{t}</span>)}
          </div>
        )}
      </div>
      {others.length > 0 && (
        <section className="kt-container pb-8">
          <SectionHeading eyebrow="Keep reading" title="More stories" />
          <div className="mt-8 grid gap-6 md:grid-cols-2">{others.map((s) => <StoryCard key={s.story_id} story={s} />)}</div>
        </section>
      )}
      <CtaBand title="Write the next story." text="Join as a customer, driver or restaurant partner." cta={{ to: "/auth/signup", label: "Get started" }} />
    </article>
  );
}
